import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Box,
  Heading,
  Text,
  Input,
  Button,
  VStack,
  HStack,
  useToast,
  Flex,
  Badge,
  Divider,
  List,
  ListItem,
  ListIcon,
  Spinner
} from '@chakra-ui/react';
import { LockIcon, UnlockIcon, CheckIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { GiPalmTree } from 'react-icons/gi';
import { useWallet } from '@solana/wallet-adapter-react';

// Mock whitelist - in a real app, this would come from your backend
const WHITELISTED_ADDRESSES = [
  "8YUkX4ckMfHwP1Kqx9SYzQDzZYMxV4JwBH6MXqZ7uDQJ", // Example address
  "GWvXgDCXRsBEMYkQQXkLm9LBbQZyxFJQygD6vLKvDcXu", // User's wallet address
  // Add more whitelisted addresses here
];

const AngelsKeyGate = ({ onAuthenticated }) => {
  // We're forcing the modal to be open by default
  const [isOpen, setIsOpen] = useState(true);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingWallet, setIsCheckingWallet] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { connected, publicKey, connecting } = useWallet();
  
  const goldColor = "#FFD700";
  
  // Check if wallet is whitelisted when connected
  useEffect(() => {
    if (connected && publicKey) {
      checkWalletAccess();
    }
  }, [connected, publicKey]);
  
  const checkWalletAccess = async () => {
    setIsCheckingWallet(true);
    setError('');
    
    try {
      // In a real app, you might want to check with your backend here
      // For now, we'll just check against our mock whitelist
      const walletAddress = publicKey.toString();
      console.log("Checking wallet access for:", walletAddress);
      
      // Simulate a delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (WHITELISTED_ADDRESSES.includes(walletAddress)) {
        toast({
          title: "Wallet Verified",
          description: "Your wallet is authorized for DAO access",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        // Close the modal
        setIsOpen(false);
        
        // Call the onAuthenticated callback if provided
        if (onAuthenticated && typeof onAuthenticated === 'function') {
          onAuthenticated();
        }
      } else {
        // If wallet is not whitelisted, show error but allow password fallback
        setError('Your wallet is not on the whitelist. Please enter the Angels Key password instead.');
      }
    } catch (err) {
      console.error("Error checking wallet access:", err);
      setError('Error verifying wallet. Please try again or use the password.');
    } finally {
      setIsCheckingWallet(false);
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    // Simulate verification delay
    setTimeout(() => {
      if (password.toLowerCase() === 'palm2') {
        // Correct password
        toast({
          title: "Access Granted",
          description: "Welcome to the Palm Heist DAO",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        // Close the modal
        setIsOpen(false);
        
        // Call the onAuthenticated callback if provided
        if (onAuthenticated && typeof onAuthenticated === 'function') {
          onAuthenticated();
        }
      } else {
        // Incorrect password
        setError('Invalid key. Please try again.');
        toast({
          title: "Access Denied",
          description: "The Angels Key could not be verified",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
      setIsLoading(false);
    }, 1500);
  };
  
  const handleConnectWallet = () => {
    // This is a placeholder for wallet connection logic
    // In a real app, you would trigger your wallet connection here
    // Removed wallet connected toast to prevent duplicates
    console.log("Wallet connected in AngelsKeyGate");
    
    // For testing, let's just grant access
    if (onAuthenticated && typeof onAuthenticated === 'function') {
      onAuthenticated();
    }
    
    setIsOpen(false);
  };
  
  const handleClose = () => {
    setIsOpen(false);
  };
  
  // If the modal is closed, don't render anything
  if (!isOpen) {
    return null;
  }
  
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      size="xl"
      isCentered
    >
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent 
        bg="#0D1019"
        borderRadius="xl"
        borderWidth="1px"
        borderColor="rgba(255, 215, 0, 0.3)"
        boxShadow={`0 0 20px rgba(255, 215, 0, 0.2), 0 0 40px rgba(255, 215, 0, 0.1)`}
        overflow="hidden"
        p={8}
        position="relative"
        zIndex="1"
      >
        <VStack spacing={6} align="center" width="100%" position="relative" zIndex="10">
          <Heading color={goldColor} size="lg" textAlign="center" letterSpacing="wider" textShadow="0 0 10px rgba(255, 215, 0, 0.5)">
            Angels Key
          </Heading>
          <Text color="white" fontSize="sm" textAlign="center">
            The exclusive access token to the Palm Heist DAO Governance
          </Text>
          
          <Divider borderColor="rgba(255, 215, 0, 0.3)" />
          
          <Box>
            <Text color="white" fontWeight="bold" mb={2}>
              Key Benefits:
            </Text>
            <List spacing={2}>
              <ListItem color="whiteAlpha.800" fontSize="sm">
                <ListIcon as={GiPalmTree} color={goldColor} />
                Exclusive voting rights on all DAO proposals
              </ListItem>
              <ListItem color="whiteAlpha.800" fontSize="sm">
                <ListIcon as={GiPalmTree} color={goldColor} />
                Revenue sharing from platform fees
              </ListItem>
              <ListItem color="whiteAlpha.800" fontSize="sm">
                <ListIcon as={GiPalmTree} color={goldColor} />
                Priority access to new features and airdrops
              </ListItem>
              <ListItem color="whiteAlpha.800" fontSize="sm">
                <ListIcon as={GiPalmTree} color={goldColor} />
                Governance over treasury allocation
              </ListItem>
            </List>
          </Box>
          
          <Button
            onClick={() => navigate('/angels-key')}
            colorScheme="yellow"
            alignSelf="center"
            fontSize="md"
            px={6}
            py={3}
            borderRadius="full"
            bg="rgba(255, 215, 0, 0.2)"
            color={goldColor}
            borderWidth="1px"
            borderColor={goldColor}
            _hover={{
              bg: "rgba(255, 215, 0, 0.3)",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(255, 215, 0, 0.3)"
            }}
            transition="all 0.2s"
          >
            Own the Heist
          </Button>
          
          {isCheckingWallet ? (
            <Box width="100%" textAlign="center" mt={4} zIndex="20">
              <Spinner color={goldColor} size="xl" mb={3} />
              <Text color="white">Verifying wallet access...</Text>
            </Box>
          ) : connected ? (
            <Box width="100%" textAlign="center" mt={4} zIndex="20">
              <HStack spacing={2} justify="center" mb={3}>
                <CheckIcon color={goldColor} />
                <Text color="white">Wallet Connected: {publicKey.toString().slice(0, 4)}...{publicKey.toString().slice(-4)}</Text>
              </HStack>
              
              {error ? (
                <form onSubmit={handleSubmit} style={{ width: '100%', position: 'relative', zIndex: 20 }}>
                  <VStack spacing={4} mt={2}>
                    <Input
                      placeholder="Enter your Angels Key password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      type="password"
                      bg="rgba(0,0,0,0.3)"
                      border="1px solid"
                      borderColor="rgba(255, 215, 0, 0.3)"
                      color="white"
                      _placeholder={{ color: "whiteAlpha.500" }}
                      _hover={{ borderColor: "rgba(255, 215, 0, 0.5)" }}
                      _focus={{ borderColor: goldColor, boxShadow: `0 0 0 1px ${goldColor}` }}
                      borderRadius="full"
                      height="40px"
                      zIndex="20"
                    />
                    
                    <Text color="red.300" fontSize="sm">
                      {error}
                    </Text>
                    
                    <HStack spacing={4} width="100%" zIndex="20">
                      <Button
                        onClick={handleClose}
                        variant="outline"
                        size="md"
                        width="50%"
                        leftIcon={<LockIcon />}
                        isDisabled={isLoading}
                        borderRadius="full"
                        borderColor="rgba(255, 255, 255, 0.3)"
                        color="white"
                        _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
                        zIndex="20"
                      >
                        Cancel
                      </Button>
                      
                      <Button
                        type="submit"
                        variant="outline"
                        size="md"
                        width="50%"
                        leftIcon={<UnlockIcon />}
                        isLoading={isLoading}
                        loadingText="Verifying..."
                        borderColor={goldColor}
                        color={goldColor}
                        _hover={{ bg: "rgba(255, 215, 0, 0.2)" }}
                        borderRadius="full"
                        zIndex="20"
                      >
                        Verify
                      </Button>
                    </HStack>
                  </VStack>
                </form>
              ) : (
                <Button
                  onClick={checkWalletAccess}
                  size="md"
                  variant="outline"
                  leftIcon={<UnlockIcon />}
                  borderColor={goldColor}
                  color={goldColor}
                  _hover={{ bg: "rgba(255, 215, 0, 0.2)" }}
                  borderRadius="full"
                  zIndex="20"
                >
                  Verify Wallet Access
                </Button>
              )}
            </Box>
          ) : (
            <Box width="100%" textAlign="center" mt={4} position="relative" zIndex="20">
              <Text color="white" mb={4}>Connect your wallet to access the DAO</Text>
              
              <Button
                onClick={handleConnectWallet}
                variant="outline"
                size="md"
                border="2px solid"
                borderColor={goldColor}
                color={goldColor}
                _hover={{
                  bg: "rgba(255, 215, 0, 0.2)",
                }}
                borderRadius="full"
                zIndex="20"
                position="relative"
              >
                Connect Wallet
              </Button>
              
              <Divider my={4} borderColor="rgba(255, 255, 255, 0.2)" />
              
              <Text color="white" mb={3} fontSize="sm">Or enter your Angels Key password</Text>
              
              <form onSubmit={handleSubmit} style={{ width: '100%', position: 'relative', zIndex: 20 }}>
                <VStack spacing={4}>
                  <Input
                    placeholder="Enter your Angels Key password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type="password"
                    bg="rgba(0,0,0,0.3)"
                    border="1px solid"
                    borderColor="rgba(255, 215, 0, 0.3)"
                    color="white"
                    _placeholder={{ color: "whiteAlpha.500" }}
                    _hover={{ borderColor: "rgba(255, 215, 0, 0.5)" }}
                    _focus={{ borderColor: goldColor, boxShadow: `0 0 0 1px ${goldColor}` }}
                    borderRadius="full"
                    height="40px"
                    zIndex="20"
                  />
                  
                  {error && (
                    <Text color="red.300" fontSize="sm">
                      {error}
                    </Text>
                  )}
                  
                  <Button
                    type="submit"
                    variant="outline"
                    size="md"
                    width="100%"
                    isLoading={isLoading}
                    loadingText="Verifying..."
                    borderColor={goldColor}
                    color={goldColor}
                    _hover={{ bg: "rgba(255, 215, 0, 0.2)" }}
                    borderRadius="full"
                    zIndex="20"
                  >
                    Verify with Password
                  </Button>
                </VStack>
              </form>
            </Box>
          )}
        </VStack>
      </ModalContent>
    </Modal>
  );
};

export default AngelsKeyGate;
